import React, { useState, useCallback } from 'react';
import { usePrayer } from '../../contexts/PrayerContext';
import { useTracker } from '../../contexts/TrackerContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useQaza } from '../../contexts/QazaContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getCurrentPrayer, getTimeRemaining, prayerNames } from '../../data/prayerData';
import {
  MapPin, RefreshCw, Check, Flame, Lock, AlertTriangle, Clock,
  ChevronDown, ChevronUp, Plane,
} from 'lucide-react';
import SEO from '../common/SEO';
import MissedPrayerDialog from '../common/MissedPrayerDialog';
import QuranVerseCard from '../home/QuranVerseCard';
import ReadQuranBox from '../home/ReadQuranBox';
import RamadanTimings from '../home/RamadanTimings';
import SignUpFlyer from '../home/SignUpFlyer';
import QuranBanner from '../home/QuranBanner';
import FeatureBanners from '../home/FeatureBanners';
import { IslamicDivider, IslamicCorners } from '../common/IslamicIcons';
import { formatDuration } from '../../utils/formatDuration';

const MISSED_THRESHOLD_MIN = 30;

const PRAYER_ACCENTS = {
  Fajr:    { light: 'from-indigo-400 to-violet-500', dark: 'from-indigo-500 to-violet-600', ring: 'ring-indigo-400', dot: '#818cf8' },
  Dhuhr:   { light: 'from-amber-400 to-orange-400',  dark: 'from-amber-500 to-orange-500',  ring: 'ring-amber-400',  dot: '#fbbf24' },
  Asr:     { light: 'from-orange-400 to-rose-400',   dark: 'from-orange-500 to-rose-500',   ring: 'ring-orange-400', dot: '#fb923c' },
  Maghrib: { light: 'from-rose-500 to-pink-500',     dark: 'from-rose-600 to-pink-600',     ring: 'ring-rose-400',   dot: '#f43f5e' },
  Isha:    { light: 'from-violet-500 to-indigo-600', dark: 'from-violet-600 to-indigo-700', ring: 'ring-violet-400', dot: '#7c3aed' },
};

const PRAYER_SYMBOLS = { Fajr: '🌙', Dhuhr: '☀', Asr: '🌤', Maghrib: '🌅', Isha: '✨' };

// Qasr indicator — which prayers are shortened in travel mode
const QASR_PRAYERS = ['Dhuhr', 'Asr', 'Isha'];

const HomePage = () => {
  const { prayerTimes, location, loading, hijriDate, refreshLocation } = usePrayer();
  const { todayPrayers, togglePrayer, batchTogglePrayers, streak, completedCount, totalPrayers, progress, currentDateKey } = useTracker();
  const { isDarkMode, language, isRTL, formatTime, travelMode } = useTheme();
  const { addQaza, isTodayQaza, isTodayQazaMadeUp } = useQaza();
  const { login, isAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  const [dialogPrayer, setDialogPrayer] = useState(null);
  const [pressedKey, setPressedKey] = useState(null);
  const [prayersExpanded, setPrayersExpanded] = useState(false);

  const { current, next } = getCurrentPrayer(prayerTimes);
  const timeRemaining = next ? getTimeRemaining(next.timeStr) : '';

  const today = new Date();
  const shortDate = today.toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
    weekday: 'short', day: 'numeric', month: 'long',
  });

  const nowMinutes = () => { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); };
  const getPrayerMinutes = useCallback((key) => {
    if (!prayerTimes?.[key]) return null;
    const [h, m] = prayerTimes[key].split(':').map(Number);
    return h * 60 + m;
  }, [prayerTimes]);

  const isPrayerTimeReached = (key) => !loading && !!prayerTimes?.[key] && nowMinutes() >= getPrayerMinutes(key);
  const isPrayerMissed = (key) => !loading && !!prayerTimes?.[key] && nowMinutes() > getPrayerMinutes(key) + MISSED_THRESHOLD_MIN;

  const getPrayerState = (key) => {
    if (todayPrayers[key]) return 'completed';
    if (isTodayQazaMadeUp(key, currentDateKey)) return 'qaza_completed';
    if (isTodayQaza(key, currentDateKey)) return 'qaza';
    if (isPrayerMissed(key)) return 'missed';
    if (isPrayerTimeReached(key)) return 'available';
    return 'upcoming';
  };

  // Travel mode prayer pair helpers
  const PRAYER_PAIRS = { Dhuhr: 'Asr', Maghrib: 'Isha' };
  const COMPANION_PRAYERS = new Set(['Asr', 'Isha']);

  const handlePrayerClick = (key) => {
    const state = getPrayerState(key);
    const companion = travelMode ? PRAYER_PAIRS[key] : null;
    if (state === 'completed') {
      setPressedKey(key); setTimeout(() => setPressedKey(null), 250);
      // Use batch to atomically mark/unmark both prayers
      if (companion) batchTogglePrayers([key, companion]);
      else togglePrayer(key);
      return;
    }
    if (state === 'missed') { setDialogPrayer({ key, formattedTime: formatTime(prayerTimes[key]) }); return; }
    if (state === 'available') {
      setPressedKey(key); setTimeout(() => setPressedKey(null), 250);
      if (companion) batchTogglePrayers([key, companion]);
      else togglePrayer(key);
    }
  };

  const handlePrayedOnTime = () => { if (dialogPrayer) { togglePrayer(dialogPrayer.key); setDialogPrayer(null); } };
  const handleMissedIt = async () => { if (dialogPrayer) { await addQaza(dialogPrayer.key, currentDateKey); setDialogPrayer(null); } };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return t('home.greeting_morning');
    if (h < 17) return t('home.greeting_afternoon');
    return t('home.greeting_evening');
  };

  const bgClass = isDarkMode
    ? 'bg-gradient-to-b from-[var(--bg-dark-via)] via-[var(--bg-dark-from)] to-[var(--bg-dark-anchor)]'
    : 'bg-gradient-to-b from-[var(--bg-light-via)] via-[var(--bg-light-from)] to-white';
  const nextAcc = next ? PRAYER_ACCENTS[next.key] : null;

  if (loading) return (
    <div className={`min-h-screen flex items-center justify-center ${bgClass}`}>
      <div className="text-center animate-fade-up">
        <div className="w-10 h-10 border-4 border-[var(--p-hex)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('home.loadingPrayers')}</p>
      </div>
    </div>
  );

  return (
    <>
      <SEO title={t('nav.home')} description={`Prayer times in ${location?.city || 'Makkah'}`} path="/" />
      <MissedPrayerDialog
        open={!!dialogPrayer} prayer={dialogPrayer?.key} prayerTime={dialogPrayer?.formattedTime}
        onPrayedOnTime={handlePrayedOnTime} onMissedIt={handleMissedIt} onClose={() => setDialogPrayer(null)}
      />

      <div className={`min-h-screen ${bgClass}`} dir={isRTL ? 'rtl' : 'ltr'}>
        {/* ── Responsive container: single-col mobile, 2-col iPad+ ────── */}
        <div className="md:max-w-4xl md:mx-auto md:px-6 md:pt-4 md:grid md:grid-cols-[1.1fr_minmax(0,0.9fr)] md:gap-6">

          {/* ══ LEFT / PRIMARY COLUMN ══ */}
          <div>
            {/* ── Top bar ── */}
            <div className="px-4 pt-3 pb-2 md:px-0">
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <button onClick={refreshLocation}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${
                    isDarkMode ? 'bg-white/10 text-gray-300 hover:bg-white/20' : 'bg-white/70 text-gray-700 hover:bg-white shadow-sm'
                  }`}>
                  <MapPin className="w-3.5 h-3.5" />
                  <span className="font-semibold">{location?.city}</span>
                  <RefreshCw className="w-3 h-3 opacity-50" />
                </button>

                <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  {/* Travel mode badge — icon only on mobile to avoid squeezing */}
                  {travelMode && (
                    <div data-testid="home-travel-badge" className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold flex-shrink-0 ${
                      isDarkMode ? 'bg-sky-500/20 text-sky-400' : 'bg-sky-100 text-sky-700'
                    }`}>
                      <Plane className="w-3 h-3" />
                      <span className="hidden sm:inline">{t('settings.travelActive')}</span>
                    </div>
                  )}
                  {/* Streak chip */}
                  <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full ${
                    streak > 0
                      ? isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
                      : isDarkMode ? 'bg-white/8 text-gray-400' : 'bg-gray-100 text-gray-500'
                  }`}>
                    <Flame className={`w-3.5 h-3.5 ${streak > 0 ? '' : 'opacity-40'}`} />
                    <span className="text-xs font-bold">
                      {streak > 0 ? `${streak} ${t('home.dayStreak')}` : t('home.streakStart')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Personal greeting */}
              {isAuthenticated && user && (
                <div className={`flex items-center gap-2 mt-2 ${isRTL ? 'flex-row-reverse' : ''} animate-fade-up`}>
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-[var(--p-hex)]/10 text-[var(--p-hex)]'
                    }`}>
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    <span className="greeting-wave">👋</span>{' '}
                    {getGreeting()}, {user.name?.split(' ')[0]}
                  </p>
                </div>
              )}
            </div>

            {/* ── Quran banner — MOVED TO TOP ── */}
            <div className="px-4 mb-2 md:px-0">
              <QuranBanner />
            </div>

            {/* ── Verse of the Day + Read Quran (two boxes) ── */}
            <div className="px-4 mb-2.5 md:px-0">
              <div className="grid grid-cols-2 gap-3 items-stretch">
                <QuranVerseCard box />
                <ReadQuranBox />
              </div>
            </div>

            {/* ── Unified prayer card ── */}
            <div className="px-4 mb-3 md:px-0">
              <div className={`rounded-2xl overflow-hidden relative ${
                isDarkMode
                  ? 'bg-white/5 backdrop-blur-md border border-white/8 shadow-xl'
                  : 'bg-white shadow-lg border border-gray-100'
              }`}>
                <IslamicCorners isDarkMode={isDarkMode} opacity={isDarkMode ? 0.14 : 0.07} />

                {/* ── Compact hero header ── */}
                {next && nextAcc && (
                  <div className="relative overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-r ${isDarkMode ? nextAcc.dark : nextAcc.light} opacity-90`} />
                    <div className="shimmer-overlay" />
                    <div className="absolute inset-0 opacity-[0.07]">
                      <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                        <defs><pattern id="hg" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                          <path d="M10 0l3 6 6 2-6 2-3 6-3-6-6-2 6-2z" fill="white"/>
                        </pattern></defs>
                        <rect width="100%" height="100%" fill="url(#hg)" />
                      </svg>
                    </div>

                    <div className={`relative z-10 px-5 py-4 flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={isRTL ? 'text-right' : ''}>
                        <p className="text-white/60 text-xs uppercase tracking-widest font-semibold mb-1">
                          {next && (() => {
                            // Use localised duration for Arabic/Urdu
                            const pm = getPrayerMinutes(next.key);
                            if (!pm) return timeRemaining.replace('until', t('home.until'));
                            const diff = pm - nowMinutes();
                            if (diff <= 0) return language === 'ar' ? 'حان الوقت' : 'It\'s time';
                            return formatDuration(diff, language, false);
                          })()}
                        </p>
                        <p className="text-white text-3xl font-black tracking-tight leading-none">
                          {travelMode && next?.key === 'Asr'
                            ? `${t('prayers.Dhuhr')} + ${t('prayers.Asr')}`
                            : travelMode && next?.key === 'Isha'
                              ? `${t('prayers.Maghrib')} + ${t('prayers.Isha')}`
                              : t(`prayers.${next.key}`)}
                        </p>
                        <p className="text-white/90 text-lg font-semibold tabular-nums mt-1" dir="ltr">
                          {formatTime(prayerTimes?.[next.key])}
                          {travelMode && QASR_PRAYERS.includes(next.key) && (
                            <span className="ml-2 text-xs font-bold bg-white/20 px-1.5 py-0.5 rounded-full">
                              {t('settings.qasr')}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className={`flex flex-col items-end gap-2 ${isRTL ? 'items-start' : ''}`}>
                        <div className={`text-right ${isRTL ? 'text-left' : ''}`}>
                          <p className="text-white/65 text-[11px] font-medium">{shortDate}</p>
                          {hijriDate && (
                            <p className="text-white/80 text-[11px] font-semibold">
                              {hijriDate.day} {language === 'ar' ? hijriDate.month.ar : hijriDate.month.en} {hijriDate.year} AH
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-28 h-2 rounded-full bg-white/25 overflow-hidden">
                            <div className="h-full rounded-full bg-white/80"
                              style={{ width: `${progress}%`, transition: 'width 0.5s ease' }} />
                          </div>
                          <span className="text-white/80 text-xs font-bold tabular-nums">{completedCount}/{totalPrayers}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Islamic divider ── */}
                <div className="px-3 pt-1 pb-1">
                  <IslamicDivider isDarkMode={isDarkMode} className="opacity-50" />
                </div>

                {/* ── Prayer rows ── */}
                <div className={`divide-y ${isDarkMode ? 'divide-white/5' : 'divide-gray-100'}`}>
                  {prayerNames.map((prayer, idx) => {
                    // In travel mode, skip companion prayers (Asr/Isha rendered under Dhuhr/Maghrib)
                    if (travelMode && COMPANION_PRAYERS.has(prayer.key)) return null;

                    const companion = travelMode ? PRAYER_PAIRS[prayer.key] : null;
                    const state = getPrayerState(prayer.key);
                    const time = prayerTimes?.[prayer.key];
                    const isNext = next?.key === prayer.key
                      || (companion && next?.key === companion);
                    const isClickable = ['available', 'missed', 'completed'].includes(state);
                    const isPressed = pressedKey === prayer.key;
                    const acc = PRAYER_ACCENTS[prayer.key];
                    const isQasrPrayer = travelMode && QASR_PRAYERS.includes(prayer.key);

                    // Sub-label using localised formatDuration
                    const pm = getPrayerMinutes(prayer.key);
                    const diffMin = pm !== null ? pm - nowMinutes() : null;
                    const subLabel = (() => {
                      if (['completed','qaza_completed','qaza'].includes(state)) return null;
                      if (state === 'missed') return formatDuration(Math.abs(diffMin || 0), language, true);
                      if (state === 'available') return t('tracker.tapToMark');
                      if (diffMin !== null && diffMin > 0) return formatDuration(diffMin, language, false);
                      return null;
                    })();

                    // COLLAPSED: show ONLY the single next prayer (not missed, not others)
                    // "Current" = the one with the dot indicator (next is non-null)
                    const isOnlyActive = isNext; // strictly the next prayer
                    if (!prayersExpanded && !isOnlyActive) {
                      return null;
                    }
                    return (
                      <button
                        key={prayer.key}
                        data-testid={`prayer-btn-${prayer.key}`}
                        data-state={state}
                        onClick={() => isClickable && handlePrayerClick(prayer.key)}
                        disabled={!isClickable}
                        className={`w-full flex items-center px-4 py-3 transition-colors ${
                          isNext
                            ? isDarkMode ? 'bg-white/6' : 'bg-[var(--bg-light-from)]/60'
                            : isClickable
                              ? isDarkMode ? 'hover:bg-white/6' : 'hover:bg-gray-50'
                              : state === 'qaza' ? '' : 'opacity-40 cursor-not-allowed'
                        } ${isRTL ? 'flex-row-reverse' : ''}`}
                        style={{
                          transform: isPressed ? 'scale(0.98)' : 'scale(1)',
                          transition: 'transform 0.15s ease, background-color 0.12s ease',
                        }}
                      >
                        {/* Icon */}
                        <div className={`relative w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isRTL ? 'ml-3' : 'mr-3'} ${
                          state === 'completed' || state === 'qaza_completed'
                            ? `bg-gradient-to-br ${acc.light}`
                            : state === 'missed' ? isDarkMode ? 'bg-amber-500/20' : 'bg-amber-50'
                            : state === 'qaza' ? isDarkMode ? 'bg-red-500/15' : 'bg-red-50'
                            : state === 'available' ? isDarkMode ? 'bg-white/10' : 'bg-gray-100'
                            : isDarkMode ? 'bg-white/5' : 'bg-gray-50'
                        }`}>
                          {(state === 'completed' || state === 'qaza_completed')
                            ? <Check className="w-4 h-4 text-white" />
                            : (state === 'missed' || state === 'qaza')
                              ? <AlertTriangle className={`w-4 h-4 ${state === 'missed' ? isDarkMode ? 'text-amber-400' : 'text-amber-500' : isDarkMode ? 'text-red-400' : 'text-red-400'}`} />
                            : state === 'upcoming'
                              ? <Lock className={`w-3.5 h-3.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                            : <span className="text-base" aria-hidden>{PRAYER_SYMBOLS[prayer.key]}</span>
                          }
                          {state === 'available' && (
                            <span className={`absolute inset-0 rounded-xl ring-1 ${acc.ring} opacity-50 animate-pulse`} />
                          )}
                          {isNext && (
                            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white"
                              style={{ background: acc.dot }} />
                          )}
                        </div>

                        {/* Name + sub-label */}
                        <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                          <span className={`font-semibold text-base ${
                            state === 'completed' || state === 'qaza_completed' ? isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                            : state === 'missed' ? isDarkMode ? 'text-amber-300' : 'text-amber-700'
                            : state === 'qaza' ? isDarkMode ? 'text-red-400' : 'text-red-500'
                            : state === 'upcoming' ? isDarkMode ? 'text-gray-400' : 'text-gray-400'
                            : isDarkMode ? 'text-white' : 'text-gray-800'
                          }`}>
                            {/* Combined label for travel mode */}
                            {companion
                              ? `${t(`prayers.${prayer.key}`)} + ${t(`prayers.${companion}`)}`
                              : t(`prayers.${prayer.key}`)
                            }
                          </span>
                          {subLabel && (
                            <bdi className={`text-xs ml-1.5 ${
                              state === 'available' ? isDarkMode ? 'text-emerald-400/80' : 'text-emerald-600/80'
                              : state === 'missed' ? isDarkMode ? 'text-amber-400/60' : 'text-amber-500/70'
                              : isDarkMode ? 'text-gray-400' : 'text-gray-400'
                            }`}>{subLabel}</bdi>
                          )}
                          {state === 'qaza_completed' && (
                            <span className={`text-xs ml-1.5 inline-flex items-center gap-0.5 ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600/70'}`}>
                              <Clock className="w-2.5 h-2.5" />{t('qaza.madeUpLabel')}
                            </span>
                          )}
                        </div>

                        {/* Time + Qasr badge */}
                        <div className={`flex items-center gap-1.5 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
                          {isQasrPrayer && (
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                              isDarkMode ? 'bg-sky-500/20 text-sky-400' : 'bg-sky-100 text-sky-600'
                            }`}>{t('settings.qasr')}</span>
                          )}
                          <span className={`text-base font-semibold tabular-nums ${
                            isNext ? isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'
                            : state === 'completed' ? isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                            : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`} dir="ltr">
                            {formatTime(time)}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* ── See all / collapse toggle ── */}
                <button
                  data-testid="toggle-prayers"
                  onClick={() => setPrayersExpanded(p => !p)}
                  className={`w-full flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold border-t transition-colors ${
                    isDarkMode ? 'border-white/5 text-gray-400 hover:text-gray-300 hover:bg-white/5' : 'border-gray-100 text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {prayersExpanded ? (
                    <><ChevronUp className="w-3.5 h-3.5" /> {t('home.collapsePrayers')}</>
                  ) : (
                    <><ChevronDown className="w-3.5 h-3.5" /> {t('home.seeAllPrayers')}</>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* ══ RIGHT / SECONDARY COLUMN (iPad+: shows next to prayers) ══ */}
          <div className="md:space-y-3 min-w-0">
            {/* Feature banners — site highlights */}
            <div className="px-4 mb-3 md:px-0 md:mb-0">
              <FeatureBanners />
            </div>

            {/* Sign-up flyer (guests only) */}
            {!isAuthenticated && (
              <div className="px-4 mb-3 md:px-0 md:mb-0">
                <SignUpFlyer onLogin={login} />
              </div>
            )}

            {/* Ramadan timings */}
            <div className="px-4 mb-3 md:px-0 md:mb-0">
              <RamadanTimings />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePage;
